import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siiRepository } from '../../infrastructure/http/sii.repository';
import { queryKeys } from '../query-keys';

/**
 * Hook para obtener el estado del certificado digital SII
 */
export const useCertificadoSiiStatus = () => {
  return useQuery({
    queryKey: queryKeys.sii.certificate(),
    queryFn: () => siiRepository.getCertificateStatus(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para obtener los folios activos del SII
 */
export const useFoliosActivosSii = () => {
  return useQuery({
    queryKey: queryKeys.sii.folios(),
    queryFn: () => siiRepository.getActiveFolios(),
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
};

/**
 * Hook para subir archivo XML de folios
 */
export const useUploadFolios = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => siiRepository.uploadFolios(file),
    onSuccess: () => {
      // Invalidar query de folios para recargar la tabla
      queryClient.invalidateQueries({ queryKey: queryKeys.sii.folios() });
    },
  });
};

/**
 * Hook para timbrar folios de boleta electrónica vía el scraper SII (solo premium).
 * Invalida los folios activos al terminar para reflejar el nuevo lote cargado.
 */
export const useTimbrarFolios = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cantidadFolios,
      desactivarLotesVigentes,
    }: {
      cantidadFolios: number;
      desactivarLotesVigentes: boolean;
    }) => siiRepository.prepararTimbraje(cantidadFolios, desactivarLotesVigentes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.sii.folios() });
    },
  });
};

/**
 * Hook para subir certificado digital
 */
export const useUploadCertificate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      file: File;
      rutRepresentative: string;
      password: string;
      repeatPassword: string;
    }) =>
      siiRepository.uploadCertificate(
        data.file,
        data.rutRepresentative,
        data.password,
        data.repeatPassword
      ),
    onSuccess: () => {
      // Invalidar query de certificado para recargar los datos
      queryClient.invalidateQueries({ queryKey: queryKeys.sii.certificate() });
    },
  });
};
